import { Box, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { ILevel } from '@sharedInterfaces/ICompanySettings';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface ProfileSkillsSectionProps {
    employee: Employee;
}

function ProfileSkillsSection({ employee }: ProfileSkillsSectionProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);

    const topSkills = [...(employee.skills || [])].sort((a, b) => b.level - a.level).slice(0, 8);

    const getLevelText = (level: number) => {
        const levelDef = levelDefinitions.find((l: ILevel) => l.level === level);
        return levelDef?.title || `Level ${level}`;
    };

    const getLevelColor = (level: number) =>
        theme.palette.levels?.[level as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Skills & Expertise
            </Typography>

            {topSkills.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    Noch keine Skills definiert
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {topSkills.map((skill) => (
                        <Box
                            key={skill.id}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    '& .skill-title': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                            onClick={() => navigate(`/${ELinks.SKILLS}/${skill.id}`)}
                        >
                            <Box
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}
                            >
                                <Typography
                                    className="skill-title"
                                    variant="body2"
                                    sx={{ fontWeight: 600, transition: 'color 0.2s' }}
                                >
                                    {skill.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: getLevelColor(skill.level), fontWeight: 600 }}
                                    >
                                        {getLevelText(skill.level)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Level {skill.level}
                                    </Typography>
                                </Box>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(skill.level / levelDefinitions.length) * 100}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: alpha(getLevelColor(skill.level), 0.1),
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: getLevelColor(skill.level),
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
}

export default ProfileSkillsSection;
